"use client";

import Image from "next/image";
import { useSearchParams } from "next/navigation";

import { Button } from "@/components/button/button.component";
import { CheckIcon } from "@/components/icons/check.icon";
import { CloseIcon } from "@/components/icons/close.icon";
import { QueryLink } from "@/components/queryLink/queryLink.component";
import { Text } from "@/components/text/text.component";

import { SHOP_DRAWER_PARAM } from "../../constants";

const VALID_DRAWER_IDS = new Set(["basePackage"]);

const BENEFITS = [
  {
    title: "All-in-one security hub",
    description:
      "The heart of your home security system, connecting smart devices for seamless protection.",
  },
  {
    title: "Intrusion detection",
    description: "",
  },
  {
    title: "ADT+ app access",
    description: "",
  },
  {
    title: "Simple setup",
    description: "Set up in minutes, no drillings required.",
  },
  {
    title: "Smart notifications",
    description:
      "Get alerts on your ADT+ app when the entry points of your home are opened or the sensors are tampered with.",
  },
  {
    title: "Add on any equipment to customize for your needs",
    description: "",
  },
];

/** Slide-out panel driven by `?drawer=` (same idea as PDP product details modal). */
export function ShopInteractiveDrawer() {
  const searchParams = useSearchParams();
  const drawerId = searchParams.get(SHOP_DRAWER_PARAM);

  if (!drawerId || !VALID_DRAWER_IDS.has(drawerId)) return null;

  return (
    <dialog
      open
      id="shop-interactive-drawer"
      data-testid="shop-interactive-drawer"
      className="fixed top-0 left-0 z-50 flex h-full w-full justify-end bg-transparent p-0">
      <QueryLink
        remove={SHOP_DRAWER_PARAM}
        className="fixed top-0 right-0 bottom-0 left-0 bg-black/40"
        aria-label="Close drawer">
        <span className="sr-only">Close drawer</span>
      </QueryLink>
      <div className="relative z-10 flex h-full w-full flex-col bg-white shadow-xl md:w-2/3 md:max-w-125 lg:w-125">
        <header className="border-greyscale-background relative flex h-[68px] shrink-0 items-center justify-between px-6 py-4">
          <Text
            variant="h5"
            component="p"
            data-testid="shop-interactive-drawer-title">
            Base Package
          </Text>
          <QueryLink remove={SHOP_DRAWER_PARAM}>
            <CloseIcon width={22} height={22} />
          </QueryLink>

          <div className="absolute right-0 bottom-0 left-0 h-[4px] w-full bg-linear-to-r from-[#0b2c5f] via-[#2f6ea5] to-[#58c0df]"></div>
        </header>

        <div className="overflow-y-auto">
          <div className="flex flex-col gap-4 px-6 py-10">
            <Text variant="h4">Start with a base package</Text>

            <Text variant="body1">
              Get full security setup with by picking from ready-made kits or
              build your own.
            </Text>
          </div>

          <div className="bg-greyscale-background mx-6 mb-10 flex flex-col items-center justify-center rounded-lg p-6">
            <Text
              variant="h5"
              component="p"
              data-testid="shop-interactive-drawer-title">
              Each system includes:
            </Text>

            <div className="mt-6 flex flex-row items-center justify-start gap-5 rounded-lg bg-white">
              <Image
                src="/assets/featured-products/Arlo-Doorbell-Camera.png"
                alt="Base Package"
                width={149}
                height={149}
              />
              <div>
                <Text variant="body2" component="p" className="mb-1">
                  Base
                </Text>
                <Text variant="body5" component="p">
                  The brains of the ADT home security system. Allows you to arm
                  and disarm your system.
                </Text>
              </div>
            </div>

            <div className="mt-6 flex flex-row items-center justify-start gap-5 rounded-lg bg-white">
              <Image
                src="/assets/featured-products/Arlo-Doorbell-Camera.png"
                alt="Base Package"
                width={149}
                height={149}
              />
              <div>
                <Text variant="body2" component="p" className="mb-1">
                  Door/Window Sensor
                </Text>
                <Text variant="body5" component="p">
                  Help protect your home's entry points with these wireless
                  sensors.
                </Text>
              </div>
            </div>

            <div className="mt-4 flex flex-row items-center justify-start gap-5">
              <Image
                src="/assets/featured-products/Arlo-Doorbell-Camera.png"
                alt="Base Package"
                width={149}
                height={149}
              />
              <div>
                <Text variant="body2" component="p" className="mb-1">
                  Plus a free yard sign and window stickers
                </Text>
                <Text variant="body5" component="p">
                  Let everyone know you’re protected by the security leader.
                </Text>
              </div>
            </div>
          </div>

          <Image
            src="/assets/base-package/base-lifestyle.png"
            alt="Base Package"
            width={708}
            height={398}
            className="w-full object-contain"
          />

          <div className="flex flex-col gap-4 px-6 py-10">
            <Text variant="h5" component="p">
              Benefits
            </Text>

            <ul className="*:body-3 my-4 *:flex *:items-start *:py-2 [&_a]:*:cursor-pointer [&_a]:*:underline">
              {BENEFITS.map((benefit) => (
                <li key={benefit.title}>
                  <div>
                    <CheckIcon width={24} height={24} />
                  </div>
                  <div>
                    <Text variant="body2" component="p">
                      {benefit.title}
                    </Text>
                    <Text variant="body3" component="p">
                      {benefit.description}
                    </Text>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <footer className="border-greyscale-background sticky bottom-0 flex flex-row items-center justify-end gap-4 border-t bg-white px-12 py-4 shadow-xl">
          <Button secondary type="button">
            Build Your System
          </Button>
          <Button secondary border type="button">
            Shop All Kits
          </Button>
        </footer>
      </div>
    </dialog>
  );
}
